import ArrowDropDownIcon from '@mui/icons-material//ArrowDropDown';
import { SortDirection, TableRow } from '@mui/material';
import { COMPANY_NAME_KEY } from 'common/constants/analyst-dashboards/analyst-dashboards';
import ExtraActionsMenu from 'components/ExtraActionsMenu';
import theme from 'theme';
import { GenericTableHeadProps } from './GenericTableHead.props';
import { calculateMinWidthFromLabel, createSortHandler, getSortIconStyle, isActive, orderHandler } from './utilities';
import WidthResizeHandler from '../ValuationsTableHead/WidthResizeHandler';
import {
  StyledTableCell,
  StyledTableHead,
  StyledTableHeadLabel,
  StyledTableSortLabel,
  StyledVisuallyHidden,
} from './index';



const GenericTableHead = ({
  order,
  orderBy,
  sortCriteria,
  onRequestSort,
  columns,
  columnWidths,
  onColumnWidthChange,
}: GenericTableHeadProps) => {
  // Get the direction for a specific column from sortCriteria
  const getColumnDirection = (columnId: string): SortDirection | false => {
    if (sortCriteria && sortCriteria.length > 0) {
      const criteria = sortCriteria.find(c => c.property === columnId);
      if (criteria) {
        return criteria.direction as SortDirection;
      }
    }
    return orderBy === columnId ? (order as SortDirection) : false;
  };

  return (
    <StyledTableHead>
      <TableRow>
        {columns.map(headCell => {
          const columnWidth = columnWidths?.[headCell.id];
          const isCompanyNameColumn = headCell.id === COMPANY_NAME_KEY;
          const columnDirection = getColumnDirection(headCell.id);
          const minWidth = calculateMinWidthFromLabel(headCell.label, headCell.isSortable);
          return (
            <StyledTableCell
              key={headCell.id}
              align={headCell.align || 'left'}
              sortDirection={orderHandler(headCell.id, false, orderBy, order, sortCriteria)}
              sx={{
                position: 'relative',
                minWidth: `${minWidth}px`,
                ...(columnWidth && { width: `${columnWidth}px` }),
                ...(isCompanyNameColumn && {
                  position: 'sticky',
                  left: 0,
                  top: 0,
                  zIndex: 11,
                  backgroundColor: 'background.paper',
                  boxShadow: '2px 0 4px rgba(0, 0, 0, 0.1)',
                }),
              }}
            >
              {headCell.isSortable && (
                <StyledTableSortLabel
                  active={isActive(headCell, orderBy, sortCriteria)}
                  direction={orderHandler(headCell.id, 'asc', orderBy, order, sortCriteria)}
                  onClick={createSortHandler(onRequestSort)(headCell.id)}
                  slotProps={getSortIconStyle(headCell, orderBy, sortCriteria)}
                  IconComponent={ArrowDropDownIcon}>
                  <StyledTableHeadLabel>{headCell.label}</StyledTableHeadLabel>
                  {isActive(headCell, orderBy, sortCriteria) && (
                    <StyledVisuallyHidden>{`sorted ${columnDirection}ending`}</StyledVisuallyHidden>
                  )}
                </StyledTableSortLabel>
              )}
              {!headCell.isSortable && (
                <StyledTableHeadLabel>{headCell.label}</StyledTableHeadLabel>
              )}
              {headCell.component === 'actions' && (
                <ExtraActionsMenu 
                  options={headCell.options?.map(option => ({
                    ...option,
                    onClick: option.onClick || option.callback
                  }))} 
                  dotsColor={theme.palette.grey[500]} 
                />
              )}
              {onColumnWidthChange && (
                <WidthResizeHandler
                  columnId={headCell.id}
                  initialWidth={columnWidth}
                  onWidthChange={onColumnWidthChange}
                />
              )}
            </StyledTableCell>
          );
        })}
      </TableRow>
    </StyledTableHead>
  );
};

export default GenericTableHead;
